import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, X, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const QRScanner = ({ onScan, onClose }) => {
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    startScanner();
    return () => {
      stopScanner();
    };
  }, []);

  const startScanner = async () => {
    try {
      setError(null);
      setScanning(true);

      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
      };

      await html5QrCode.start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          // Extract cart code from QR data
          let cartCode = decodedText.trim();
          let isQrId = false;  // Flag to indicate if this is a UUID-based QR ID
          
          // Handle URL format: https://example.com/cart/UUID or https://example.com/carts/CART001
          if (decodedText.includes('http://') || decodedText.includes('https://')) {
            // Try to extract UUID from URL path like /cart/de89b655-68ca-4d65-92a2-3172652f5644
            const uuidMatch = decodedText.match(/\/cart[s]?\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
            if (uuidMatch) {
              cartCode = uuidMatch[1].toLowerCase();
              isQrId = true;
              console.log('[QRScanner] Detected UUID QR ID:', cartCode);
            } else {
              // Try to extract from URL path like /carts/CART001
              const pathMatch = decodedText.match(/\/carts?\/([^/?]+)/i);
              if (pathMatch) {
                cartCode = pathMatch[1].toUpperCase();
              } else {
                // Try to extract from query param like ?cart=CART001
                const queryMatch = decodedText.match(/[?&]cart=([^&]+)/i);
                if (queryMatch) {
                  cartCode = queryMatch[1].toUpperCase();
                } else {
                  // Try to find CART pattern anywhere in URL
                  const cartMatch = decodedText.match(/(CART\d+)/i);
                  if (cartMatch) {
                    cartCode = cartMatch[1].toUpperCase();
                  }
                }
              }
            }
          } else if (decodedText.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
            // Plain UUID format
            cartCode = decodedText.toLowerCase();
            isQrId = true;
            console.log('[QRScanner] Detected plain UUID:', cartCode);
          } else if (decodedText.match(/^CART\d+$/i)) {
            // Plain cart ID format: CART001
            cartCode = decodedText.toUpperCase();
          } else if (decodedText.includes('CART')) {
            // Contains CART prefix somewhere
            const match = decodedText.match(/(CART\d+)/i);
            if (match) cartCode = match[1].toUpperCase();
          }
          
          console.log('[QRScanner] Decoded:', decodedText, '-> Cart Code:', cartCode, 'isQrId:', isQrId);

          stopScanner();
          // Pass both cartCode and isQrId to the callback
          onScan(cartCode, isQrId);
        },
        (errorMessage) => {
          // Ignore scan errors (no QR detected)
        }
      );
    } catch (err) {
      console.error("Scanner error:", err);
      setScanning(false);
      
      if (err.name === 'NotAllowedError') {
        setError('Camera access denied. Please allow camera access or enter cart code manually.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found. Please enter cart code manually.');
      } else {
        setError('Could not start camera. Please enter cart code manually.');
      }
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
    setScanning(false);
  };

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <h2 className="text-white font-semibold text-lg">Scan Cart QR Code</h2>
        <Button
          data-testid="close-scanner-btn"
          variant="ghost"
          size="icon"
          className="text-white hover:bg-white/10"
          onClick={handleClose}
        >
          <X className="w-6 h-6" />
        </Button>
      </div>

      {/* Scanner Area */}
      <div className="flex-1 flex items-center justify-center p-4">
        {error ? (
          <Card className="max-w-sm w-full bg-white/10 border-white/20">
            <CardContent className="p-6 text-center">
              <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <p className="text-white mb-4">{error}</p>
              <Button
                onClick={handleClose}
                variant="secondary"
              >
                Enter Code Manually
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="relative">
            {/* Scanner Container */}
            <div 
              id="qr-reader" 
              ref={scannerRef}
              className="w-[300px] h-[300px] overflow-hidden rounded-2xl"
            />
            
            {/* Scanner Frame Overlay */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 border-2 border-white/30 rounded-2xl" />
              
              {/* Corner indicators */}
              <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-primary rounded-tl-xl" />
              <div className="absolute top-0 right-0 w-12 h-12 border-t-4 border-r-4 border-primary rounded-tr-xl" />
              <div className="absolute bottom-0 left-0 w-12 h-12 border-b-4 border-l-4 border-primary rounded-bl-xl" />
              <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-primary rounded-br-xl" />
              
              {/* Scanning line animation */}
              {scanning && (
                <div className="absolute left-4 right-4 h-0.5 bg-primary animate-scan" />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="p-6 text-center">
        <p className="text-white/70 text-sm">
          Point your camera at the QR code on the shopping cart
        </p>
        <Button
          variant="ghost"
          className="text-white/50 hover:text-white mt-2"
          onClick={handleClose}
        >
          Or enter code manually
        </Button>
      </div>

      <style jsx>{`
        @keyframes scan {
          0%, 100% { top: 10%; }
          50% { top: 85%; }
        }
        .animate-scan {
          animation: scan 2s ease-in-out infinite;
        }
        #qr-reader video {
          border-radius: 1rem !important;
        }
      `}</style>
    </div>
  );
};

export default QRScanner;
